import { useRef, useState } from 'react';
import { isSection, type SurveyItem } from '@voice2me/shared';
import { ExcelFormatDialog } from './ExcelFormatDialog.js';
import {
  promptToOpenText,
  readWorkbook,
  rowsToQuestions,
  type ImportOutcome,
} from './questionImport/index.js';

type Props = {
  onImport: (items: SurveyItem[]) => void;
};

/** Splits pasted text into question prompts on commas and new lines, dropping blanks. */
export function parsePrompts(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** A " across N sections" suffix for the import summary, or "" when nothing was grouped. */
function describeSections(items: SurveyItem[]): string {
  const count = items.filter(isSection).length;
  return count > 0 ? ` across ${count} section${count === 1 ? '' : 's'}` : '';
}

/**
 * Bulk-adds questions to a draft survey, either from an uploaded Excel file (which can carry the
 * full range of question types — see {@link ExcelFormatDialog}) or from a quick comma/newline
 * paste that becomes free-text questions. Spreadsheet parsing lives in ./questionImport so this
 * component stays focused on the interaction.
 */
export function ImportQuestions({ onImport }: Props) {
  const [text, setText] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<string[][] | null>(null);
  const [includeSection, setIncludeSection] = useState(false);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const prompts = parsePrompts(text);

  /** Parse the already-loaded rows under the current column layout, surfacing an empty result. */
  function parseRows(sourceRows: string[][], withSection: boolean) {
    const result = rowsToQuestions(sourceRows, { hasSectionColumn: withSection });
    if (result.questionCount === 0 && result.issues.length === 0) {
      setOutcome(null);
      setFileError('No questions found in that file.');
      return;
    }
    setFileError(null);
    setOutcome(result);
  }

  async function handleFile(file: File) {
    setFileName(file.name);
    setFileError(null);
    setOutcome(null);
    setRows(null);
    try {
      const loaded = await readWorkbook(file);
      setRows(loaded);
      parseRows(loaded, includeSection);
    } catch {
      setFileError('That file could not be read. Save it as .xlsx and try again.');
    }
  }

  function toggleSectionColumn(next: boolean) {
    setIncludeSection(next);
    if (rows) parseRows(rows, next);
  }

  function resetFile() {
    setOutcome(null);
    setFileName(null);
    setRows(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className="import-questions">
      <div className="import-questions-header">
        <strong>Import questions</strong>
        <button type="button" className="button" onClick={() => setGuideOpen(true)}>
          How does import work?
        </button>
      </div>

      <label>
        From an Excel spreadsheet
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
      </label>
      <p className="note">
        One question per row. See “How does import work?” for the exact column layout.
      </p>

      <label className="inline">
        <input
          type="checkbox"
          checked={includeSection}
          onChange={(event) => toggleSectionColumn(event.target.checked)}
        />
        Include section column
      </label>
      <p className="note">
        When ticked, column 2 is the section title and the response type moves to column 3. Leave a
        section cell blank to keep that question at the top level.
      </p>

      {fileError && <p role="alert">{fileError}</p>}

      {outcome && (
        <div className="import-summary">
          <p className="note">
            {outcome.questionCount > 0
              ? `${outcome.questionCount} question${outcome.questionCount === 1 ? '' : 's'}${describeSections(outcome.items)} ready from ${fileName}.`
              : `No usable questions in ${fileName}.`}
            {outcome.skipped > 0 &&
              ` ${outcome.skipped} row${outcome.skipped === 1 ? '' : 's'} skipped.`}
          </p>
          {outcome.issues.length > 0 && (
            <ul className="import-issues">
              {outcome.issues.map((issue) => (
                <li key={`${issue.row}-${issue.message}`}>
                  Row {issue.row}: {issue.message}
                </li>
              ))}
            </ul>
          )}
          <div className="import-summary-actions">
            <button
              type="button"
              disabled={outcome.questionCount === 0}
              onClick={() => {
                onImport(outcome.items);
                resetFile();
              }}
            >
              Add {outcome.questionCount} question{outcome.questionCount === 1 ? '' : 's'}
            </button>
            <button type="button" onClick={resetFile}>
              Clear
            </button>
          </div>
        </div>
      )}

      <label>
        Or paste free-text questions
        <textarea
          rows={3}
          value={text}
          placeholder="How are you finding the new shift pattern?, What would make your day easier?"
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      <p className="note">
        Separate with commas or new lines. Each becomes its own free-text question.
      </p>
      <button
        type="button"
        disabled={prompts.length === 0}
        onClick={() => {
          onImport(prompts.map(promptToOpenText));
          setText('');
        }}
      >
        {prompts.length > 0
          ? `Add ${prompts.length} question${prompts.length === 1 ? '' : 's'}`
          : 'Import'}
      </button>

      <ExcelFormatDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
